import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React, { JSX } from 'react';
import {
  Button,
  Card,
  CardContent,
  Check,
  Grid,
  GridColumn,
  Select,
  Separator,
  Text,
  Tooltip,
  ISelectOption,
  TooltipToggle,
  AnalyticsTrigger,
  Heading,
  Modal
} from 'telia-front-react';
import GravityForm from '../forms/GravityForm';
import MobileLifeStore, { CALLS_PACKAGE_ID, CampaignInfo } from '../stores/MobileLifeStore';
import { formatPrice, getPrivateMobileLifeBasePrice, mobileLifePricePerUser, mobileLifeTotalPrice, renderMonthlyPrice } from '../util/PriceUtil';
import { PrivateMobileCardProps } from './PrivateMobileCard';

interface PrivateMobileLifeModalProps {
  mobileLifePackages: PrivateMobileCardProps[];
  mobileLifeStore?: MobileLifeStore;
}

interface PrivateMobileLifeModalState {
  formActive: boolean;
}

@observer
class MobileLifeOrderModal extends React.Component<PrivateMobileLifeModalProps, PrivateMobileLifeModalState> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  constructor(props: PrivateMobileLifeModalProps) {
    super(props);
    this.state = { formActive: true };
  }

  private getMobilePackageDataOptions = (mobilePackages: PrivateMobileCardProps[]): ISelectOption[] => {
    const { getPackageId } = this.props.mobileLifeStore;
    return mobilePackages.map((mobilePackage) => ({ label: mobilePackage.packageName, value: getPackageId(mobilePackage.packageIds) }));
  };

  private getMobilePackage = (): PrivateMobileCardProps => {
    const { getPackageId, selectedMobilePackage } = this.props.mobileLifeStore;
    return this.props.mobileLifePackages.find((mobilePackage) => getPackageId(mobilePackage.packageIds) === selectedMobilePackage);
  };

  private getCallsPrice = (): number => {
    const { mobileLifeStore } = this.props;
    const selectedPackage = this.getMobilePackage();

    const packageCallsPrices = mobileLifeStore.getPackagePrices(CALLS_PACKAGE_ID);
    return mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID) ? packageCallsPrices.price : selectedPackage.callsPackagePrice;
  };

  private hasInternetDiscount = (): boolean => {
    const { mobileLifeStore } = this.props;
    const selectedPackage = this.getMobilePackage();
    return mobileLifeStore.hasPackageDiscount(mobileLifeStore.getPackageId(selectedPackage.packageIds));
  };

  private hasCampaign = (): boolean => {
    const { mobileLifeStore } = this.props;
    return !this.hasInternetDiscount() && !mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID) && this.getMobilePackage().campaigns?.length > 0;
  };

  private getBasePrice = (): number => {
    const { mobileLifeStore } = this.props;
    const internetPrices = mobileLifeStore.getPackagePrices(mobileLifeStore.selectedMobilePackage);
    const selectedPackage = this.getMobilePackage();

    const hasCallsDiscount = mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID);
    const hasDiscount = this.hasInternetDiscount() || hasCallsDiscount;

    return getPrivateMobileLifeBasePrice(
      mobileLifeStore.selectedDeviceCount,
      internetPrices?.price,
      selectedPackage.multiUserDistributedPrice,
      selectedPackage.singleUserDistributedPrice,
      selectedPackage.packageCustomDistributedPrices,
      hasDiscount
    );
  };

  private getCampaignPrice = (campaign: CampaignInfo): number => {
    const { mobileLifeStore } = this.props;
    const hasCallsDiscount = mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID);
    const hasDiscount = this.hasInternetDiscount() || hasCallsDiscount;
    const internetPrices = mobileLifeStore.getPackagePrices(mobileLifeStore.selectedMobilePackage);
    const selectedPackage = this.getMobilePackage();
    return getPrivateMobileLifeBasePrice(
      mobileLifeStore.selectedDeviceCount,
      internetPrices?.price,
      campaign?.multiUserDistributedCampaignPrice || selectedPackage.multiUserDistributedPrice,
      campaign?.singleUserDistributedCampaignPrice || selectedPackage.singleUserDistributedPrice,
      campaign?.packageCustomDistributedCampaignPrices || selectedPackage.packageCustomDistributedPrices,
      hasDiscount
    );
  };

  private renderPriceItem = (text: string, price: React.ReactNode, isDiscount = false): JSX.Element => {
    const translate = this.context.translate;
    return (
      <>
        <GridColumn width={['xs-8', 'md-6']}>{text}</GridColumn>
        <GridColumn className={`text-right neutral ${isDiscount ? 'text-error' : ''}`} width={['xs-4', 'md-6']}>
          {isDiscount ? `-${price}` : price} {translate('mobile.eurPerMonth')}
        </GridColumn>
      </>
    );
  };

  private renderPackageAndUserSelection = (): JSX.Element => {
    const { mobileLifeStore, mobileLifePackages } = this.props;
    const translate = this.context.translate;
    const selectedPackageOption = this.getMobilePackageDataOptions(mobileLifePackages).find((mp) => mp.value === mobileLifeStore.selectedMobilePackage);
    const selectedDeviceCount = mobileLifeStore.selectedDeviceCount;
    const selectedPackage = this.getMobilePackage();

    return (
      <Grid>
        <GridColumn width={['xs-12', 'md-6']}>
          <Select
            id="mobile-life-data-selector"
            label={translate('mobile.package.selectedPackage')}
            value={selectedPackageOption.value}
            onChange={(newValue) => mobileLifeStore.setSelectedMobilePackage(newValue as string)}
            simpleValue={true}
            options={this.getMobilePackageDataOptions(this.props.mobileLifePackages)}
          />
        </GridColumn>
        <GridColumn width={['xs-12', 'md-6']}>
          <Select
            id="mobile-life-user-selection"
            label={translate('mobile.package.user.chosen.amount')}
            value={selectedDeviceCount.toString()}
            onChange={(newValue) => mobileLifeStore.setSelectedDeviceCount(+newValue, selectedPackage.packageIds)}
            simpleValue={true}
            options={['1', '2', '3', '4', '5'].map((devices) => ({ label: devices, value: devices }))}
          />
        </GridColumn>
      </Grid>
    );
  };

  private renderGravityForm = (): JSX.Element => {
    const translate = this.context.translate;
    const selectedPackage = this.getMobilePackage();
    const {
      mobileLifeStore: { selectedDeviceCount, setOrderModalOpen }
    } = this.props;

    return (
      <>
        <p>{translate('mobile.package.modal.check.text')}</p>
        <GravityForm
          submissionCustomGaData={{ eventLabel: `${selectedDeviceCount} SIMi - ${selectedPackage.packageName}`, eventCategory: 'Mobiilne elu' }}
          name={selectedPackage.formName}
          fieldValueProviders={[
            { technicalParameter: 'device-count', fixedValue: true, data: selectedDeviceCount.toString() },
            { technicalParameter: 'package-name', fixedValue: true, data: selectedPackage.packageName }
          ]}
          submitButtonProps={{
            type: 'primary'
          }}
          onClose={() => setOrderModalOpen(false)}
        />
      </>
    );
  };

  private renderSingleUserOrderButton = (): JSX.Element => {
    const translate = this.context.translate;
    const selectedPackage = this.getMobilePackage();

    return (
      <AnalyticsTrigger
        trigger={{
          callback: 'onClick',
          id: `CTA - ${selectedPackage.packageName} mobiilipakett`
        }}
      >
        <Button text={translate('mobile.package.interested')} iconRight="#arrow-right" url={selectedPackage.singleUserUrl} />
      </AnalyticsTrigger>
    );
  };

  private renderInBrackets = (content: React.ReactNode): JSX.Element => {
    return <> ({content})</>;
  };

  private renderCampaignPreTextAndTooltip = (campaign: CampaignInfo, isSecondOrOnlyCampaign: boolean) => {
    const { mobileLifeStore } = this.props;
    const tooltipText = campaign.tooltipText;
    const translate = this.context.translate;
    const callsPrice = this.getCallsPrice();
    const selectedDeviceCount = mobileLifeStore.selectedDeviceCount;
    const mobileLifeTotalPricePerMonth = `${campaign.customPricePreText ?? ''} ${translate(`mobile.package.campaign.eurPerMonth`, {
      price: mobileLifeTotalPrice(this.getCampaignPrice(campaign), callsPrice, selectedDeviceCount).toFixed(2)
    })}`.trim();
    const mobileLifeTotalPricePerUserPerMonth = ` (${formatPrice(mobileLifePricePerUser(this.getCampaignPrice(campaign), mobileLifeStore.selectedDeviceCount))} ${translate('mobile.package.user')})`;

    return tooltipText ? (
      <p>
        <p>{campaign.endDate ? campaign.endDate : ''}</p>
        <span>
          <Tooltip
            referenceElement={
              <TooltipToggle className={isSecondOrOnlyCampaign ? 'h4 text-campaign text-bold' : 'h4 text-bold'}>{mobileLifeTotalPricePerMonth}</TooltipToggle>
            }
          >
            {tooltipText}
          </Tooltip>
        </span>
        <span>{mobileLifeTotalPricePerUserPerMonth}</span>
      </p>
    ) : (
      <p>
        <p>{campaign.endDate ? campaign.endDate : ''}</p>
        <span className={isSecondOrOnlyCampaign ? 'h4 text-campaign text-bold' : 'h4 text-bold'}>{mobileLifeTotalPricePerMonth}</span>
        <span>{mobileLifeTotalPricePerUserPerMonth}</span>
      </p>
    );
  };

  private renderPackageTotalPrice = (): JSX.Element => {
    const { mobileLifeStore } = this.props;
    const translate = this.context.translate;

    const hasInternetDiscount = this.hasInternetDiscount();
    const internetPrices = mobileLifeStore.getPackagePrices(mobileLifeStore.selectedMobilePackage);

    const basePrice = this.getBasePrice();
    const callsPrice = this.getCallsPrice();
    const selectedDeviceCount = mobileLifeStore.selectedDeviceCount;

    const totalPrice = mobileLifeTotalPrice(basePrice, callsPrice, selectedDeviceCount);
    if (hasInternetDiscount) {
      return (
        <p>
          <span className="h4 text-bold text-error">{renderMonthlyPrice(true, totalPrice, translate)}</span>
          <span>
            {this.renderInBrackets(
              translate('mobile.internet.regularPrice', {
                price: mobileLifeTotalPrice(internetPrices?.priceWithoutDiscount, callsPrice, selectedDeviceCount).toFixed(2)
              })
            )}
          </span>
        </p>
      );
    }
    if (this.hasCampaign()) {
      return (
        <Text>
          {this.getMobilePackage().campaigns?.length === 2 ? (
            <>
              <p>{this.renderCampaignPreTextAndTooltip(this.getMobilePackage().campaigns[0], false)}</p>
              <p>{this.renderCampaignPreTextAndTooltip(this.getMobilePackage().campaigns[1], true)}</p>
            </>
          ) : (
            <>{this.renderCampaignPreTextAndTooltip(this.getMobilePackage().campaigns[0], true)}</>
          )}
          <span className="text-inactive">
            {translate('mobile.internet.regularPrice', {
              price: mobileLifeTotalPrice(internetPrices?.priceWithoutDiscount ?? basePrice, callsPrice, selectedDeviceCount).toFixed(2)
            })}
          </span>
        </Text>
      );
    }
    return (
      <p>
        <span className="h4 text-bold">{renderMonthlyPrice(true, totalPrice, translate)}</span>
        <span className="text-light text-bold">
          {' '}
          ({formatPrice(mobileLifePricePerUser(totalPrice, selectedDeviceCount))} {translate('mobile.package.user')})
        </span>
      </p>
    );
  };

  public render() {
    const translate = this.context.translate;
    const {
      mobileLifeStore: { selectedDeviceCount, orderModalOpen, setOrderModalOpen }
    } = this.props;
    const selectedPackage = this.getMobilePackage();

    if (!selectedPackage) {
      return null;
    }

    return (
      <AnalyticsTrigger trigger={{ callback: 'onClose', id: `Modal - Close order modal - ${selectedPackage.packageName}` }}>
        <Modal size="8-col" isOpen={orderModalOpen} onClose={() => setOrderModalOpen(false)} aria-labelledby={translate('mobile.package.modal.mobile.life')}>
          <Text>
            <Heading className="h3">{translate('mobile.package.modal.mobile.life')}</Heading>
            <Separator spacing="small" color="invisible" />
            {!!selectedPackage.modalExtraContent && <>{selectedPackage.modalExtraContent}</>}
            <Card padding="medium">
              <CardContent background="neutral-bg-level-2">
                {this.renderPackageAndUserSelection()}
                <Separator spacing="xsmall" color="invisible" />
                <p>{translate('mobile.package.modal.price')}</p>
                <Grid horizontalGutter="none">
                  <GridColumn>{this.renderPackageTotalPrice()}</GridColumn>
                </Grid>
              </CardContent>
            </Card>
            <Separator spacing="xsmall" color="invisible" />
            {selectedDeviceCount > 1 && (
              <Check
                id="multiple-devices-check"
                value="yes"
                labelText={translate('mobile.package.modal.check')}
                checked={!this.state.formActive}
                onChange={(checked) => this.setState({ formActive: !checked })}
              />
            )}
            {this.state.formActive && selectedDeviceCount > 1 ? this.renderGravityForm() : this.renderSingleUserOrderButton()}
          </Text>
        </Modal>
      </AnalyticsTrigger>
    );
  }
}

export default inject('mobileLifeStore')(MobileLifeOrderModal);
